import { defineNuxtPlugin } from '#imports'
import PanelLink from '../components/panel/PanelLink.vue'
import { toPanelEventProps } from '@/lib/panelEvents'
import { usePanelStore, parseHash } from '~/stores/usePanelStore'

export default defineNuxtPlugin((nuxtApp) => {
  const $panel: PanelPlugin = {
    // Typed, preferred API: open a panel by its registry key.
    open(key: string, props: Record<string, unknown> = {}) {
      return usePanelStore().open(key as any, props)
    },

    // In-place swap of the current top panel (same instance id, new key/props).
    replace(key: string, props: Record<string, unknown> = {}) {
      usePanelStore().replace(key as any, props)
    },

    // Close the current top panel (respects shouldDismiss unless force = true).
    close(force?: boolean) {
      return usePanelStore().close(undefined, force)
    },

    // Pop the stack down to the given index (index 0 = only root left; -1 = all).
    popTo(index: number) {
      return usePanelStore().popTo(index)
    },
    popToRoot() {
      return usePanelStore().popToRoot()
    },
    popToClear() {
      return usePanelStore().popToClear()
    },

    // Path-based entry point: opens a panel from a "/customer/edit?id=123"
    // style path. Looks up the matching registry key and forwards to store.open.
    // Callers that want type-checked panel keys should use $panel.open() directly.
    create(path: string, options: { props?: Record<string, any>; events?: Record<string, any> } = {}) {
      const stripped = path.startsWith('/') ? path.slice(1) : path
      const parsed = parseHash('#' + stripped)
      if (parsed) {
        const eventProps = toPanelEventProps(options.events)
        const merged = { ...parsed.props, ...(options.props || {}), ...eventProps }
        usePanelStore().open(parsed.key, merged)
        return
      }
      console.error(`[panel] "${path}" does not match any registered panel.`)
    },
  }

  nuxtApp.provide('panel', $panel)
  nuxtApp.vueApp.component((PanelLink as any).name || 'PanelLink', PanelLink)
})

export interface PanelPlugin {
  open(key: string, props?: Record<string, unknown>): any
  replace(key: string, props?: Record<string, unknown>): void
  close(force?: boolean): any
  popTo(index: number): void
  popToRoot(): void
  popToClear(): void
  create(path: string, options?: { props?: Record<string, any>; events?: Record<string, any> }): void
}
